<script lang="ts">
  /**
   * Painel "Análise visual" — agrupa os gráficos analíticos do dashboard.
   * Layout responsivo: stack em mobile, 2 colunas em desktop pra alguns gráficos.
   */
  import type { DiagnosticResult } from '$core/diagnostic.types';
  import Card from '../../components/Card.svelte';
  import EmptyState from '../../components/EmptyState.svelte';
  import CostFlowChart from './charts/CostFlowChart.svelte';
  import CorrectionEffortChart from './charts/CorrectionEffortChart.svelte';
  import BalanceChart from './charts/BalanceChart.svelte';
  import AbsenceChart from './charts/AbsenceChart.svelte';
  import PunchHeatmap from './charts/PunchHeatmap.svelte';
  import TopExcessChart from './charts/TopExcessChart.svelte';
  import { buildDailyJourneys } from '$core/metrics/journeys';
  import {
    buildDailyAbsenceAnalytics,
    calcAbsenceChartKpis,
  } from '$core/metrics/dailyAbsenceAnalytics';

  export let result: DiagnosticResult;
  export let assumptions: { toleranceMinutes: number; dailyHours: number; monthlyHours: number; correctionMinutesPerEvent: number; excessiveJourneyMinutes: number; shortIntradayBreakMinutes: number; shortInterjourneyMinutes: number };

  $: analytics = result.metrics.analytics;
  $: hasData = analytics !== undefined && analytics.daily.length > 0;
  $: hasCostBaseline = analytics?.hasCostBaseline ?? false;
  $: hasOvertimeRanking = (result.metrics.cost.topExcessByEmployee?.items?.length ?? 0) > 0;

  // AbsenceChart usa dados normalizados (% de colaboradores afetados por dia).
  // Recomputamos a partir das jornadas — mesmo cálculo que alimentou as métricas.
  $: absenceJourneys = hasData ? buildDailyJourneys(result.dataset, assumptions) : [];
  $: absencePoints = hasData ? buildDailyAbsenceAnalytics(absenceJourneys, assumptions.toleranceMinutes) : [];
  $: absenceKpis = calcAbsenceChartKpis(absencePoints);
</script>

<div class="analytics">
  {#if !hasData || !analytics}
    <Card>
      <EmptyState message="Sem dados suficientes para gerar gráficos analíticos no período importado." />
    </Card>
  {:else}
    {#if hasCostBaseline}
      <Card padding="lg">
        <CostFlowChart daily={analytics.daily} />
      </Card>
      <Card padding="lg">
        <CorrectionEffortChart
          daily={analytics.daily}
          correctionMinutesPerEvent={analytics.correctionMinutesPerEvent}
          hourlyRateBrl={analytics.hourlyRateBrl}
        />
      </Card>
    {:else}
      <Card padding="md">
        <div class="cost-cta">
          <h3 class="cost-cta__title">Quer ver o custo diário do processo de ponto?</h3>
          <p class="cost-cta__body">
            Informe o salário bruto médio ou o valor-hora nas premissas para
            destravar o gráfico de custo diário (hora extra + atrasos + correções).
          </p>
        </div>
      </Card>
    {/if}

    <Card padding="lg">
      <BalanceChart daily={analytics.daily} />
    </Card>

    <Card padding="lg">
      <AbsenceChart points={absencePoints} kpis={absenceKpis} />
    </Card>

    <div class="analytics__row">
      <Card padding="lg">
        <PunchHeatmap heatmap={analytics.punchHeatmap} />
      </Card>

      {#if hasOvertimeRanking}
        <Card padding="lg">
          <TopExcessChart
            ranked={result.metrics.cost.topExcessByEmployee}
            employees={result.dataset.employees}
          />
        </Card>
      {/if}
    </div>
  {/if}
</div>

<style>
  .analytics {
    display: flex;
    flex-direction: column;
    gap: var(--ptt-space-4);
  }
  .analytics__row {
    display: grid;
    grid-template-columns: 1fr;
    gap: var(--ptt-space-4);
  }
  @media (min-width: 960px) {
    .analytics__row {
      grid-template-columns: 1.3fr 1fr;
    }
  }
  .cost-cta {
    display: flex;
    flex-direction: column;
    gap: var(--ptt-space-2);
  }
  .cost-cta__title {
    margin: 0;
    font-size: 1rem;
    font-weight: 700;
    color: var(--ptt-gray-300);
  }
  .cost-cta__body {
    margin: 0;
    font-size: 0.875rem;
    color: var(--ptt-gray-200);
    line-height: 1.45;
  }
</style>
